import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import '../stylesheets/studyDashboard.scss'
import { Button, FixedTooltip, Loader } from 'components/UIElements'
import { browserHistory } from 'utils/navigation'
import STRINGS from 'utils/strings'
import StudyDBHeader from './StudyDBHeader'
import ComplianceChart from './ComplianceChart'
import PerformanceChart from '../containers/PerformanceChartContainer'
import DataGapChart from '../containers/DataGapChartContainer'
import { INSIGHTS_VENDORS } from '../utils/utils'

const isEmbeddableVendor = vendorType =>
  [INSIGHTS_VENDORS.holistics, INSIGHTS_VENDORS.superset].includes(vendorType)

class StudyDashboardPage extends React.Component {
  constructor(props) {
    super(props)
    this.state = { tooltipProps: { visible: false } }
  }

  fetchDashboardData = () => {
    const {
      fetchComplianceData,
      fetchInstrumentData,
      fetchEventData,
      hasReadInstrumentsPermission,
      hasReadParticipantPermission,
      insights,
      isEnroller,
      params,
      participantEvents,
    } = this.props
    const { studyID, siteID } = params
    if (!insights) {
      if (!isEnroller) {
        if (hasReadInstrumentsPermission) fetchInstrumentData(studyID)
        if (hasReadParticipantPermission) {
          fetchComplianceData(studyID)
          fetchEventData(studyID, siteID, participantEvents.start, participantEvents.end)
        }
      } else if (hasReadParticipantPermission) {
        fetchEventData(studyID, siteID, participantEvents.start, participantEvents.end)
      }
    }
  }

  componentDidMount() {
    const { resetStudyDashboard } = this.props
    document.addEventListener('scroll', this.closeTooltip)
    resetStudyDashboard()
    this.fetchDashboardData()
  }

  componentDidUpdate(prevProps) {
    const { params, resetStudyDashboard } = this.props
    const prevStudyID = prevProps.params?.studyID
    const prevVersionID = prevProps.params?.studyVersionID
    if (params?.studyID !== prevStudyID || params?.studyVersionID !== prevVersionID) {
      resetStudyDashboard()
      this.fetchDashboardData()
    }
  }

  componentWillUnmount() {
    document.removeEventListener('scroll', this.closeTooltip)
  }

  closeTooltip = () => {
    const { tooltipProps } = this.state
    if (tooltipProps.visible) {
      this.updateTooltip({ visible: false })
    }
  }

  updateTooltip = props => {
    this.setState({ tooltipProps: props })
  }

  renderInsightsLink = data => {
    const { params, openInsightsDashboard } = this.props
    const { studyID, studyVersionID } = params
    if (!isEmbeddableVendor(data.vendor_type)) return null
    return (
      <Button
        link
        className='linkt-button'
        icon='fas fa-external-link-alt'
        content={data.button.content}
        onClick={() => openInsightsDashboard({ dashboard: data, studyID, studyVersionID })}
      />
    )
  }

  renderInsightsDashboard = () => {
    const {
      insights,
      hasEditInsightsPermission,
      studyRoleId,
      params,
      loadInsightsDashboards,
    } = this.props
    const { studyID, studyVersionID } = params
    const { custom_buttons: customButtons = {}, permissions: insightsPermissions = [] } = insights || {}

    if (loadInsightsDashboards) {
      return <Loader active />
    }

    const visibleDashboards = Object.values(customButtons).filter(dashboard => {
      if (!isEmbeddableVendor(dashboard?.vendor_type)) return false
      const permission = insightsPermissions.find(
        item => item.dashboard_name === dashboard.dashboard_name && String(studyID) === String(item.study_id),
      )
      if (!permission) return true
      return !permission.hidden_role_ids?.[studyRoleId]
    })

    return (
      <div className='insights-dashboard'>
        {hasEditInsightsPermission && (
          <div className='flexed-header'>
            <div />
            <Button
              content='Edit Properties'
              onClick={() =>
                browserHistory.push(`/studies/${studyID}/${studyVersionID}/compliance/insights-permissions`)
              }
            />
          </div>
        )}
        {visibleDashboards.length === 0 ? (
          <div className='insights-unavailable'>
            <span>
              <b>{STRINGS.insightsDashboardUnavailable}</b>
            </span>
          </div>
        ) : (
          visibleDashboards.map(dashboard => (
            <div key={dashboard.dashboard_name} className='insights-container'>
              {this.renderInsightsLink(dashboard)}
              {dashboard.heading && <h5>{dashboard.heading}</h5>}
              {dashboard.body && <p>{dashboard.body}</p>}
            </div>
          ))
        )}
      </div>
    )
  }

  render() {
    const {
      changeChartType,
      compliance,
      hasReadInstrumentsPermission,
      hasReadParticipantPermission,
      header,
      insights,
      isEnroller,
      params,
    } = this.props
    const { siteID, studyID, studyVersionID } = params
    const { tooltipProps } = this.state
    return (
      <div className='study-dashboard page'>
        {insights ? (
          this.renderInsightsDashboard()
        ) : (
          <>
            <FixedTooltip {...tooltipProps} />
            <StudyDBHeader {...header} />
            <div className='flexed start-aligned'>
              {hasReadParticipantPermission && (
                <ComplianceChart
                  {...compliance}
                  studyID={studyID}
                  studyVersionID={studyVersionID}
                  siteID={siteID}
                  onChangeType={changeChartType}
                  insights={insights}
                  isEnroller={isEnroller}
                />
              )}
              {hasReadInstrumentsPermission && (
                <PerformanceChart
                  updateTooltip={this.updateTooltip}
                  studyID={studyID}
                  studyVersionID={studyVersionID}
                  siteID={siteID}
                  insights={insights}
                  isEnroller={isEnroller}
                />
              )}
            </div>
            {hasReadParticipantPermission && (
              <DataGapChart updateTooltip={this.updateTooltip} studyID={params.studyID} siteID={params.siteID} />
            )}
          </>
        )}
      </div>
    )
  }
}

StudyDashboardPage.propTypes = {
  studyRoleId: PropTypes.number,
  hasEditInsightsPermission: PropTypes.bool,
  changeChartType: PropTypes.func,
  compliance: PropTypes.object,
  fetchComplianceData: PropTypes.func,
  fetchEventData: PropTypes.func,
  fetchInstrumentData: PropTypes.func,
  hasReadInstrumentsPermission: PropTypes.bool,
  hasReadParticipantPermission: PropTypes.bool,
  isEnroller: PropTypes.bool,
  header: PropTypes.object,
  insights: PropTypes.shape({
    custom_buttons: PropTypes.object,
    base_url: PropTypes.string,
    permissions: PropTypes.array,
  }),
  loadInsightsDashboards: PropTypes.bool,
  openInsightsDashboard: PropTypes.func,
  params: PropTypes.object,
  participantEvents: PropTypes.shape({
    end: PropTypes.instanceOf(moment),
    start: PropTypes.instanceOf(moment),
  }),
  resetStudyDashboard: PropTypes.func,
}

export default StudyDashboardPage
